// First-visit mini how-to ("Welcome popup"). Shown once per browser (7-day
// expiry, see lib/onceFlag.ts) — the localStorage flag is set ONLY when the
// user clicks "Let's go", so an accidental reload before reading it shows
// it again. No backdrop / ESC close on purpose: the single CTA is the only
// way out, which also guarantees the flag gets set. Version suffix in the
// key lets us re-show after major UI overhauls.

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { hasSeenOnceFlag, markOnceFlagSeen } from '@/lib/onceFlag';
import styles from './WelcomeHowTo.module.css';

const SEEN_KEY = 'vs-welcome-howto-v1';

export function hasSeenWelcomeHowTo(): boolean {
  return hasSeenOnceFlag(SEEN_KEY);
}

interface Props {
  /** Talent name from the session — personalizes the title when present. */
  name?: string;
  onDismiss(): void;
}

export function WelcomeHowTo({ name, onDismiss }: Props) {
  const [closing, setClosing] = useState(false);

  const handleLetsGo = () => {
    markOnceFlagSeen(SEEN_KEY);
    setClosing(true);
    // Let the fade-out play before unmounting.
    window.setTimeout(onDismiss, 180);
  };

  return (
    <div
      className={`${styles.backdrop} ${closing ? styles.closing : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="How this tool works"
    >
      <div className={styles.panel}>
        <h2 className={styles.title}>
          Welcome{name ? `, ${name}` : ''}!
        </h2>
        <p className={styles.intro}>Quick tour — three steps to your delivery:</p>

        <div className={styles.steps}>
          <div className={styles.step}>
            <i className="ph-duotone ph-magic-wand" aria-hidden="true" />
            <div>
              <strong>Generate</strong>
              <span>
                Scroll down to the text blocks, tweak text &amp; settings, and
                generate takes — up to 5 per block. Split the text into
                multiple blocks to fine-tune voice settings per sentence.
              </span>
            </div>
          </div>
          <div className={styles.step}>
            <i className="ph-fill ph-star" aria-hidden="true" />
            <div>
              <strong>Star your favorite</strong>
              <span>
                Click the star on the best take of each block — starred takes
                appear on the timeline above, where you can drag them into
                position.
              </span>
            </div>
          </div>
          <div className={styles.step}>
            <i className="ph-duotone ph-cloud-arrow-up" aria-hidden="true" />
            <div>
              <strong>Upload</strong>
              <span>
                Happy with the result? Hit <strong>Upload Version</strong> —
                everything is delivered automatically.
              </span>
            </div>
          </div>
        </div>

        <p className={styles.hint}>
          Need details later? The <i className="ph-bold ph-question" aria-hidden="true" />{' '}
          icon at the top has the full guide.
        </p>

        <div className={styles.actions}>
          <Button variant="primary" onClick={handleLetsGo}>
            Let&apos;s go
          </Button>
        </div>
      </div>
    </div>
  );
}
